import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="submit-progress"
//
// Paced progress for a submit whose real cost the browser cannot observe. Saving a backdated
// movement replays every allocation from that day onwards: seconds to minutes of server work,
// with no intermediate response to hook into. Without this the user stares at a frozen page.
//
// TWO CLOCKS, AND THE REAL ONE ALWAYS WINS.
//
// The steps are paced by an ESTIMATE: a fixed baseSeconds for the save itself, plus
// secondsPerDayValue for every day of history the insert forces the allocator to replay. Both
// come from measurement — 1.8s for a save with no replay, and a production plant day holding
// ~2100 WasteFormMovement rows at ~0.8ms each to recreate, times a margin for the production
// database. The estimate decides nothing; it only spaces the labels out.
//
// The real clock is the request. finish() runs on the first completion signal to arrive and stops
// everything at once, whichever step the estimate had reached — so a save with nothing to do does
// not have to sit through the choreography. Nothing here ever holds the overlay open on its own.
//
// The grace period is the other half of that: for the first graceMs the overlay is not shown at
// all. A quick save finishes inside it and the user sees no modal, no steps, no flash.
//
// The last step never completes on its own: once the estimate runs out it keeps spinning until a
// completion signal arrives. An estimate that runs short shows a longer "saving", never a finished
// checklist while the server is still working.
export default class extends Controller {
  static targets = ["overlay", "step", "dateField"]
  static values = {
    secondsPerDay: { type: Number, default: 4 },
    baseSeconds: { type: Number, default: 2 },
    graceMs: { type: Number, default: 400 },
    latestDate: String
  }

  connect() {
    this.timers = []
    this.running = false
  }

  disconnect() {
    this.clearTimers()
  }

  start() {
    if (!this.hasOverlayTarget || this.stepTargets.length === 0) return

    this.running = true
    this.startedAt = performance.now()
    console.log("[SubmitProgress] submit, estimate", this.estimatedSeconds(), "s")

    // Nothing is shown until the grace period has passed: a save that finishes inside it must
    // leave no trace on screen.
    this.timers.push(setTimeout(() => this.reveal(), this.graceMsValue))
  }

  // Every completion signal lands here. turbo:submit-end covers a Turbo form submission,
  // turbo:before-render and turbo:before-visit cover the page being swapped, pagehide covers a
  // native navigation. Whichever arrives first wins; the rest are no-ops.
  finish() {
    if (!this.running) return

    this.running = false
    this.clearTimers()
    console.log("[SubmitProgress] finished after", Math.round(performance.now() - this.startedAt), "ms")

    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
    this.stepTargets.forEach((step) => this.resetStep(step))
  }

  reveal() {
    if (!this.running) return

    this.overlayTarget.classList.remove("hidden")

    const stepMs = (this.estimatedSeconds() * 1000) / this.stepTargets.length
    this.activate(this.stepTargets[0])

    this.stepTargets.slice(1).forEach((step, index) => {
      this.timers.push(setTimeout(() => {
        this.complete(this.stepTargets[index])
        this.activate(step)
      }, stepMs * (index + 1)))
    })
  }

  // baseSeconds is what a save costs with no replay at all; every replayed day adds
  // secondsPerDay on top. Both were measured, not guessed — see the class comment.
  estimatedSeconds() {
    return this.baseSecondsValue + this.backdatedDays() * this.secondsPerDayValue
  }

  // Days of history the save has to replay: from the dated movement up to the plant's MOST RECENT
  // movement, which is the stretch the replay actually walks. Not up to today — measured in
  // development, a movement 15 days old by the calendar was only 6 days of history, and the
  // estimate came out 16x too long. The two coincide on a plant registered up to date and diverge
  // on one that is not, which is exactly when a wrong estimate is most visible.
  backdatedDays() {
    if (!this.hasDateFieldTarget) return 0

    const dated = this.parseDate(this.dateFieldTarget.value)
    if (!dated) return 0

    const reference = this.parseDate(this.latestDateValue) || new Date()
    const midnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

    return Math.max(0, Math.round((midnight(reference) - midnight(dated)) / 86400000))
  }

  // "2026-08-31" parses as UTC and can land a day early west of Greenwich, so build a date-only
  // value locally. A datetime-local value ("2026-08-25T10:00") already parses as local.
  parseDate(value) {
    if (!value) return null

    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnly) return new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3])

    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  clearTimers() {
    this.timers.forEach(clearTimeout)
    this.timers = []
  }

  activate(step) {
    step.classList.remove("text-gray-400")
    step.classList.add("text-gray-900", "font-medium")
    this.showState(step, "active")
  }

  complete(step) {
    step.classList.remove("font-medium")
    step.classList.add("text-gray-500")
    this.showState(step, "done")
  }

  resetStep(step) {
    step.classList.remove("text-gray-900", "text-gray-500", "font-medium")
    step.classList.add("text-gray-400")
    this.showState(step, "pending")
  }

  showState(step, state) {
    step.querySelectorAll("[data-state]").forEach((icon) => {
      icon.classList.toggle("hidden", icon.dataset.state !== state)
    })
  }
}
